import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import NavBar from './NavBar';
import '../styles/CourseDetail.css';

const CourseDetail = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [courseData, setCourseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [language, setLanguage] = useState('ko'); // 언어 설정 추가
    
    // 코스 데이터 (CourseList와 동일)
    const allCourses = [
        {
            id: 'soccer-card-making',
            title: '축구 카드 만들기',
            description: 'Python과 HTML/CSS로 나만의 축구 카드를 만들어요',
            enable: true
        },
        {
            id: 'soccer-card-collection',
            title: '축구 카드 수집',
            description: '카드 수집 게임을 만들며 조건문과 확률을 배워요',
            enable: false
        },
        {
            id: 'lotto-number',
            title: '로또 번호 추천',
            description: '랜덤과 통계를 활용한 번호 생성기를 만들어요',
            enable: false
        },
        {
            id: 'pokemon-card-making',
            title: '포켓몬 카드 만들기',
            description: '나만의 포켓몬 카드를 디자인하고 만들어요',
            enable: false
        }
    ];

    useEffect(() => {
        // meta.json에서 코스 데이터 로드
        const loadCourseData = async () => {
            try {
                const response = await fetch(`/courses/${courseId}/meta.json`);
                if (!response.ok) {
                    throw new Error('Course not found');
                }

                const metaData = await response.json();
                setCourseData(metaData);
                setLoading(false);
            } catch (error) {
                console.error('Failed to load course:', error);
                setLoading(false);
            }
        };

        loadCourseData();
    }, [courseId]);

    // 언어별 텍스트 가져오기 헬퍼 함수
    const getText = (obj) => {
        if (typeof obj === 'string') return obj;
        return obj[language] || obj['ko'] || '';
    };

    if (loading) {
        return <div className="loading">코스 정보를 불러오는 중...</div>;
    }

    if (!courseData) {
        return <div className="error">코스를 찾을 수 없습니다.</div>;
    }

    const handleStartCourse = () => {
        navigate(`/courses/${courseId}/step/1`);
    };

    // 간단한 카드 렌더링 함수
    const renderSimpleCard = (cardData) => {
        return (
            <div style={{
                width: '250px',
                border: '3px solid #333',
                borderRadius: '10px',
                padding: '20px',
                backgroundColor: '#f0f0f0',
                fontFamily: 'Arial, sans-serif'
            }}>
                <h2 style={{
                    textAlign: 'center',
                    backgroundColor: '#333',
                    color: 'white',
                    margin: '-20px -20px 20px -20px',
                    padding: '10px',
                    borderRadius: '7px 7px 0 0'
                }}>
                    {cardData.name} {cardData.isCaptain && '⭐'}
                </h2>

                <div style={{ marginBottom: '15px' }}>
                    <div style={{ color: '#666', fontSize: '14px' }}>등번호</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{cardData.number}</div>
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <div style={{ color: '#666', fontSize: '14px' }}>포지션</div>
                    <div style={{ fontSize: '18px', color: '#e74c3c' }}>{cardData.position}</div>
                </div>

                <div style={{ marginBottom: '15px' }}>
                    <div style={{ color: '#666', fontSize: '14px' }}>팀</div>
                    <div style={{ fontSize: '18px' }}>{cardData.team}</div>
                </div>

                {cardData.isCaptain && (
                    <div style={{
                        backgroundColor: '#FFD700',
                        padding: '5px',
                        textAlign: 'center',
                        borderRadius: '5px',
                        fontWeight: 'bold'
                    }}>
                        주장
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="course-detail-container">
            <NavBar />
            
            {/* Hero Section */}
            <section className="hero-section">
                <button className="back-button" onClick={() => navigate('/courses')}>
                    ← 코스 목록
                </button>

                <div className="hero-content">
                    <div className="hero-info">
                        <h1 className="course-title">{getText(courseData.title)}</h1>
                        <p className="course-description">{getText(courseData.longDescription)}</p>

                        <div className="course-info">
                            <span className="info-item">
                                📚 {courseData.steps.length}단계
                            </span>
                            <span className="info-item">
                                ⏱️ {courseData.metadata.estimatedTime}
                            </span>
                            <span className="info-item">
                                🎯 {courseData.metadata.difficulty === 'beginner' ? '초급' :
                                    courseData.metadata.difficulty === 'intermediate' ? '중급' : '고급'}
                            </span>
                        </div>

                        <button className="start-button" onClick={handleStartCourse}>
                            학습 시작하기 →
                        </button>
                    </div>

                    <div className="hero-preview">
                    <div className="preview-container">
                        <div className="preview-before">
                            <h4>Before</h4>
                            <pre>{getText(courseData.preview.before)}</pre>
                        </div>
                        <div className="preview-arrow">→</div>
                        <div className="preview-after">
                            <h4>After</h4>
                            {courseData.preview.afterType === 'simple-card' ? (
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    {renderSimpleCard(courseData.preview.afterData)}
                                </div>
                            ) : (
                                <img src={courseData.preview.afterImage} alt="완성 예시" />
                            )}
                        </div>
                    </div>
                    </div>
                </div>
            </section>

            {/* Tabs Section */}
            <section className="tabs-section">
                <div className="tabs">
                    <button
                        className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        개요
                    </button>
                    <button
                        className={`tab ${activeTab === 'curriculum' ? 'active' : ''}`}
                        onClick={() => setActiveTab('curriculum')}
                    >
                        커리큘럼
                    </button>
                    <button
                        className={`tab ${activeTab === 'concepts' ? 'active' : ''}`}
                        onClick={() => setActiveTab('concepts')}
                    >
                        학습 개념
                    </button>
                </div>

                <div className="tab-content">
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <div className="overview-content">
                            <h2>🎯 무엇을 만들까요?</h2>
                            <p>{getText(courseData.longDescription)}</p>

                            <h3>✨ 이런 걸 배워요</h3>
                            <ul>
                                <li>Python으로 데이터 다루기 (변수, 딕셔너리)</li>
                                <li>HTML로 웹페이지 구조 만들기</li>
                                <li>CSS로 예쁜 디자인 추가하기 (색상, 테두리, 배경)</li>
                                <li>조건문으로 특별한 표시 추가하기</li>
                            </ul>

                            <h3>🎨 최종 결과물</h3>
                            <p>
                                나만의 축구 선수 카드를 만들고, 여러 선수로 이루어진
                                드림팀 갤러리를 완성합니다!
                            </p>
                        </div>
                    )}

                    {/* Curriculum Tab */}
                    {activeTab === 'curriculum' && (
                        <div className="curriculum-content">
                            <h2>📚 {courseData.steps.length}단계 커리큘럼</h2>
                            <div className="steps-timeline">
                                {courseData.steps.map((step, index) => (
                                    <div key={step.id} className="step-item">
                                        <div className="step-number">{step.id}</div>
                                        <div className="step-info">
                                            <h4>{getText(step.title)}</h4>
                                            <div className="step-meta">
                        <span className="step-type">
                          {step.type === 'article' ? '📖 읽기' : '💻 코딩'}
                        </span>
                                                <span className="step-duration">⏱️ {step.duration}</span>
                                            </div>
                                        </div>
                                        {index < courseData.steps.length - 1 && (
                                            <div className="step-connector" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Concepts Tab */}
                    {activeTab === 'concepts' && (
                        <div className="concepts-content">
                            <h2>💡 학습 개념</h2>
                            <div className="concepts-grid">
                                {courseData.concepts.map((concept, index) => (
                                    <div key={index} className="concept-card">
                                        <span className="concept-icon">{concept.icon}</span>
                                        <h4>{getText(concept.name)}</h4>
                                        <p>{getText(concept.description)}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Next Courses Section */}
            {(() => {
                // Get related courses from allCourses
                const relatedCourseIds = [];
                if (courseData.nextCourse) relatedCourseIds.push(courseData.nextCourse);
                if (courseData.relatedCourses) relatedCourseIds.push(...courseData.relatedCourses);
                
                const enabledRelatedCourses = relatedCourseIds
                    .map(id => allCourses.find(course => course.id === id))
                    .filter(course => course && course.enable);
                
                if (enabledRelatedCourses.length === 0) return null;
                
                return (
                    <section className="next-courses-section">
                        <h2>🚀 다음 추천 코스</h2>
                        <div className="next-courses">
                            {enabledRelatedCourses.map(course => (
                                <div 
                                    key={course.id}
                                    className="next-course-card" 
                                    onClick={() => navigate(`/courses/${course.id}`)}
                                >
                                    <h4>{course.title}</h4>
                                    <p>{course.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                );
            })()}
        </div>
    );
};

export default CourseDetail;